import moment from "moment-timezone";

import { getRGBFromHex } from "helpers/color";

export const dateRangePresets = [
  {
    key: "today",
    text: "Today",
    value: "today",
  },
  {
    key: "yesterday",
    text: "Yesterday",
    value: "yesterday",
  },
  {
    key: "previous7days",
    text: "Previous 7 Days",
    value: "previous7days",
  },
  {
    key: "previous30days",
    text: "Previous 30 Days",
    value: "previous30days",
  },
  {
    key: "currentMonth",
    text: "Current Month",
    value: "currentMonth",
  },
  {
    key: "previousMonth",
    text: "Previous Month",
    value: "previousMonth",
  },
  {
    key: "currentQuarter",
    text: "Current Quarter",
    value: "currentQuarter",
  },
  {
    key: "previousQuarter",
    text: "Last Quarter",
    value: "previousQuarter",
  },
  {
    key: "yearToDate",
    text: "Year To Date",
    value: "yearToDate",
  },
  {
    key: "all",
    text: "All Time",
    value: "all",
  },
];

export const dateRangePresetValues = dateRangePresets.map(({ value }) => value);

export const callTypeOptions = [
  { key: "connected", value: "connected", text: "Connected" },
  { key: "not_connected", value: "not_connected", text: "Not Connected" },
  { key: "voicemail", value: "voicemail", text: "Voicemail" },
  { key: "callback", value: "callback", text: "Callback" },
];

export const callsPerDayOperatorOptions = [
  {
    key: "gt",
    text: "Greater Than",
    value: "gt",
  },
  {
    key: "lt",
    text: "Less Than",
    value: "lt",
  },
];

export const durationOperatorOptions = [
  {
    key: "gt",
    text: "Greater Than",
    value: "gt",
  },
  {
    key: "lt",
    text: "Less Than",
    value: "lt",
  },
];

export const callDispositionOptions = [
  { key: "contains", value: "contains", text: "Contains" },
  { key: "excludes", value: "excludes", text: "Excludes" },
];

export const DATE_FORMAT = "YYYY-MM-DD";
const beginningOfTime = moment("2000-01-01").format(DATE_FORMAT);
const today = moment().format(DATE_FORMAT);
const yesterday = moment().subtract(1, "days").format(DATE_FORMAT);
const _7daysAgo = moment().subtract(7, "days").format(DATE_FORMAT);
const _30daysAgo = moment().subtract(30, "days").format(DATE_FORMAT);
const startOfMonth = moment().startOf("month").format(DATE_FORMAT);
const endOfMonth = moment().endOf("month").format(DATE_FORMAT);
const startOfYear = moment().startOf("year").format(DATE_FORMAT);
const startOfPreviousMonth = moment()
  .subtract(1, "months")
  .startOf("month")
  .format(DATE_FORMAT);
const endOfPreviousMonth = moment()
  .subtract(1, "months")
  .endOf("month")
  .format(DATE_FORMAT);
const startOfQuarter = moment().startOf("quarter").format(DATE_FORMAT);
const endOfQuarter = moment().endOf("quarter").format(DATE_FORMAT);
const startOfPreviousQuarter = moment()
  .subtract(1, "Q")
  .startOf("quarter")
  .format(DATE_FORMAT);
const endOfPreviousQuarter = moment()
  .subtract(1, "Q")
  .endOf("quarter")
  .format(DATE_FORMAT);

export const datePresets = {
  yearToDate: {
    start: startOfYear,
    end: today,
  },
  today: {
    start: today,
    end: today,
  },
  yesterday: {
    start: yesterday,
    end: yesterday,
  },
  previous7days: {
    start: _7daysAgo,
    end: today,
  },
  previous30days: {
    start: _30daysAgo,
    end: today,
  },
  previousMonth: {
    start: startOfPreviousMonth,
    end: endOfPreviousMonth,
  },
  currentMonth: {
    start: startOfMonth,
    end: endOfMonth,
  },
  currentQuarter: {
    start: startOfQuarter,
    end: endOfQuarter,
  },
  previousQuarter: {
    start: startOfPreviousQuarter,
    end: endOfPreviousQuarter,
  },
  all: {
    start: beginningOfTime,
    end: today,
  },
};

// Ruvixx brand colors anchoring the chart gradient (warm → neutral → cool):
// Orange Dark → Primary Orange → Brand Grey → Teal → Blue. Routing the
// orange→teal bridge through a grey keeps the mid-range neutral instead of the
// muddy olive that direct warm→cool RGB blending produces. The grey is the one
// brand family that differs by theme, so it adapts to stay legible on both
// light and dark backgrounds; the saturated stops are theme-independent.
const BRAND_GREY = { light: "#878787", dark: "#b0b0b0" };

function brandGradientStops(isDark) {
  const grey = isDark ? BRAND_GREY.dark : BRAND_GREY.light;
  return ["#F34A02", "#FF6B35", grey, "#24B388", "#057B9F"];
}

/**
 * Deterministic categorical color for a chart series, sampled from the Ruvixx
 * brand gradient. Series are spread evenly along the warm→neutral→cool stops so
 * the palette stays on-brand rather than a full-spectrum rainbow. Keyed by
 * position (not random), so a series keeps its color across refetches.
 */
export function chartColor(index, count, isDark) {
  const stops = brandGradientStops(isDark);
  const t = count > 1 ? index / (count - 1) : 0;
  const scaled = t * (stops.length - 1);
  const lo = Math.floor(scaled);
  const hi = Math.min(lo + 1, stops.length - 1);
  const blend = scaled - lo;

  const from = getRGBFromHex(stops[lo]);
  const to = getRGBFromHex(stops[hi]);
  const mix = (a, b) => Math.round(a + (b - a) * blend);
  return `rgb(${mix(from.r, to.r)}, ${mix(from.g, to.g)}, ${mix(from.b, to.b)})`;
}
